import { json, type ActionFunctionArgs } from '@remix-run/node';
import { z } from 'zod';
import { createConversationMessage } from '~/modules/chat/api/create-conversation-message.server';
import {
    MAX_NUM_ATTACHMENTS,
    validateChatUploadFile,
} from '~/modules/chat/utils/validation';

export const CREATE_CONVERSATION_MESSAGE_API =
    '/remix-api/chat/create-conversation-message';

const FORM_DATA_SCHEMA = z
    .object({
        conversationID: z.coerce.number().int().positive(),
        message: z.string().trim().nullish(),
        attachments: z
            .array(
                z.instanceof(File).refine(
                    (file) => {
                        const result = validateChatUploadFile(file);
                        return result.valid;
                    },
                    { message: 'Invalid file' }
                )
            )
            .max(MAX_NUM_ATTACHMENTS)
            .nullish(),
    })
    .refine(
        ({ message, attachments }) => {
            return !!message || !!attachments?.length;
        },
        {
            message: 'Message or attachments are required',
        }
    );

/**
 * API endpoint to create a message in an enquiry conversation.
 * Use with the `useConversation` hook.
 * @see {@link packages/web/app/modules/chat/hook/useConversation.ts}
 */
export const action = async ({ context, request }: ActionFunctionArgs) => {
    const { logger } = context;
    const formData = await request.formData();
    const conversationID = formData.get('conversationID');
    const message = formData.get('message');
    const attachments = formData.getAll('attachments');
    const result = FORM_DATA_SCHEMA.safeParse({
        conversationID,
        message,
        attachments,
    });

    if (!result.success) {
        return json(null, { status: 400 });
    }

    try {
        const message = await createConversationMessage({
            context,
            conversationID: result.data.conversationID,
            message: result.data.message ?? undefined,
            attachments: result.data.attachments ?? undefined,
        });

        if (!message) {
            return json(null, { status: 500 });
        }

        return json({ message });
    } catch (error) {
        logger.error(`Error in ${CREATE_CONVERSATION_MESSAGE_API}`, {
            error,
            data: result.data,
        });
        return json(null, { status: 500 });
    }
};
